import { useQueries, useQuery } from '@tanstack/react-query';
import { useBrandFilter } from '@/components/brand-filter/brand-filter-context';
import { bankTransfersClient } from '@/lib/bank-transfers-client';
import type { OrderBoardCard } from '@/lib/order-board-client';
import { posProvidersClient } from '@/lib/pos-providers-client';
import { BOARD_QUERY_ROOT, LANE_REFETCH_INTERVAL_MS } from './board/use-order-board';
import { isTransferReviewCandidate, TRANSFER_REVIEW_STATUSES, transferReviewOrderIds } from './transfer-review';

/**
 * Deliberately NOT under `BOARD_QUERY_ROOT`: every drop invalidates that root, and a card move says
 * nothing about a brand's POS integration.
 */
const POS_QUERY_ROOT = 'board-pos-providers';

/** A POS is connected or disconnected from settings, not mid-shift — no need to re-ask on every mount. */
const POS_STALE_TIME_MS = 5 * 60_000;

/** One page of each status is the board's working set — a day with more open receipts is not one we expect. */
const TRANSFER_REVIEW_PAGE_SIZE = 100;

/**
 * The cached "does this brand have an enabled POS?" query — line sessions only: in an aggregate
 * session the route answers 423 and the session catalogue carries the answer (`ScopedBrand.hasPosProvider`).
 *
 * `retryOnMount: false`: a failed lookup has no data, and TanStack refetches such a query whenever
 * a new observer mounts, putting it back to `pending` meanwhile. Every card list and detail panel
 * that mounts would then flip the answer back to "unknown" and re-ask; it settles as "no POS" once.
 */
function brandHasPosQuery(brandId: string) {
  return {
    queryKey: [POS_QUERY_ROOT, brandId],
    queryFn: () => posProvidersClient.getProviders(brandId),
    select: (providers: Awaited<ReturnType<typeof posProvidersClient.getProviders>>) =>
      providers.some((provider) => provider.enabled),
    staleTime: POS_STALE_TIME_MS,
    retryOnMount: false,
  };
}

/**
 * Whether the brand has an enabled POS: undefined while the answer is in flight, and false when it
 * failed — a failed lookup reads as "no POS", like the app shell's own. An aggregate session reads
 * the session catalogue and never asks.
 */
export function useBrandHasPos(brandId: string | null | undefined): boolean | undefined {
  const { isAggregate, brandById } = useBrandFilter();
  const query = useQuery({ ...brandHasPosQuery(brandId ?? ''), enabled: !!brandId && !isAggregate });
  if (!brandId) return undefined;
  if (isAggregate) return !!brandById.get(brandId)?.hasPosProvider;
  if (query.isError) return false;
  return query.data;
}

interface BoardTransferReviewOptions {
  /** Every card the board holds — only placed transfer orders are looked at. */
  cards: readonly OrderBoardCard[];
  /** Aggregate sessions only: the brand filter's selection. */
  brandIds?: string[];
  /**
   * The bank-transfers surface is on for this session. Off, `GET /bank-transfers` answers 403 — and
   * a polled 403 is an `auth:unauthorized` every tick, which ends in a logout.
   */
  bankTransfersEnabled: boolean;
}

/**
 * The ids of the board cards to badge with `orders:transferReview.badge`. The cards carry no
 * transfer, so the brand's `pending` and `mismatch` menteo-order transfers are listed and joined to
 * them (see `transferReviewOrderIds`) — only when a card's brand has no enabled POS, else nothing
 * is fetched.
 *
 * No `from`: the list filters on the transfer's own `createdAt`, so an older receipt an operator
 * linked to today's order would fall outside the board's window and its card would lose the badge
 * the detail still shows. `source: 'menteo_order'` keeps POS and unattributed receipts from filling
 * the page instead.
 *
 * Under `BOARD_QUERY_ROOT` and on the lanes' interval, so a move, the refresh button and the poll
 * reload the receipts together with the cards they mark.
 */
export function useBoardTransferReview({
  cards,
  brandIds,
  bankTransfersEnabled,
}: BoardTransferReviewOptions): Set<string> {
  const candidateBrands = [
    ...new Set(cards.filter(isTransferReviewCandidate).flatMap((card) => (card.brandId ? [card.brandId] : []))),
  ].sort();

  // An aggregate session reads the session catalogue: the POS route answers 423 there.
  const { isAggregate, brandById } = useBrandFilter();
  const posAnswers = useQueries({
    queries: isAggregate ? [] : candidateBrands.map((brandId) => brandHasPosQuery(brandId)),
  });
  const brandsWithoutPos = new Set(
    candidateBrands.filter((brandId, index) =>
      isAggregate
        ? !brandById.get(brandId)?.hasPosProvider
        : posAnswers[index].isError || posAnswers[index].data === false,
    ),
  );

  const transferAnswers = useQueries({
    queries: TRANSFER_REVIEW_STATUSES.map((status) => ({
      queryKey: [BOARD_QUERY_ROOT, 'transfer-review', status, brandIds?.join(',') ?? ''],
      queryFn: async () => {
        const response = await bankTransfersClient.list({
          status,
          source: 'menteo_order',
          brandIds,
          limit: TRANSFER_REVIEW_PAGE_SIZE,
        });
        return response.data.items;
      },
      enabled: bankTransfersEnabled && brandsWithoutPos.size > 0,
      refetchInterval: LANE_REFETCH_INTERVAL_MS,
    })),
  });

  return transferReviewOrderIds(
    cards,
    transferAnswers.flatMap((answer) => answer.data ?? []),
    brandsWithoutPos,
  );
}
